<template>
  <div :class="{ fullscreen: isFullscreen }" class="barcode-scanner" ref="scanner" @mousedown="startDrag"
    @touchstart="startDrag" :style="scannerStyle" aria-label="Barcode Scanner">
    <div class="scanner-container">
      <video ref="video" class="scanner" @loadedmetadata="setVideoDimensions"></video>
      <div class="barcode-mask" v-if="showBarcodeHighlight"></div>
    </div>
    <div class="guide-line"></div>
    <button @click="toggleFullscreen" class="fullscreen-btn">{{ isFullscreen ? 'Minimize' : 'Fullscreen' }}</button>
    <div class="success-flash" v-if="showSuccessFlash"></div>
    <div class="sku-overlay" v-if="showSkuOverlay">{{ scannedSku }}</div>
    <div class="scan-instructions" v-if="showScanInstructions"></div>
  </div>
</template>

<script>
import { BrowserMultiFormatReader, BarcodeFormat } from '@zxing/library';

export default {
  data() {
    return {
      isFullscreen: false,
      isDragging: false,
      startX: 0,
      startY: 0,
      initialX: 0,
      initialY: 0,
      showBarcodeHighlight: true,
      showSuccessFlash: false,
      showSkuOverlay: false,
      scannedSku: '',
      codeReader: null,
      originalPosition: { x: 0, y: 0, width: 0, height: 0 },
      lastScanned: '',
      lastScanTime: 0,
      debounceTimeout: null,
      showScanInstructions: true,
    };
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    widthRem: {
      type: String,
      default: '20',
    },
    heightRem: {
      type: String,
      default: '10',
    },
  },
  emits: ['scan', 'fullscreen-changed'],
  computed: {
    scannerStyle() {
      return {
        width: this.isFullscreen ? `calc(100vw - ${this.widthRem}rem)` : `${this.widthRem}rem`,
        height: this.isFullscreen ? '100vh' : `${this.heightRem}rem`,
      };
    },
  },
  methods: {
    async startCamera() {
      if (this.codeReader) this.stopCamera();

      const formats = [
        BarcodeFormat.CODE_128,
        BarcodeFormat.QR_CODE,
        BarcodeFormat.CODE_39,
        BarcodeFormat.EAN_13,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.EAN_8,
        BarcodeFormat.ITF,
        BarcodeFormat.CODABAR,
      ];

      try {
        this.codeReader = new BrowserMultiFormatReader(null, formats);
        const videoInputDevices = await this.codeReader.listVideoInputDevices();
        const selectedDeviceId = videoInputDevices[0].deviceId;

        const constraints = {
          video: {
            deviceId: selectedDeviceId
          },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        const videoElement = this.$refs.video;
        videoElement.srcObject = stream;
        videoElement.onloadedmetadata = () => videoElement.play();

        this.codeReader.decodeFromVideoDevice(selectedDeviceId, videoElement, (result, error) => {
          if (result) {
            this.onDecode(result.text);
          } else if (error) {
            if (error.name !== 'NotFoundException' && !error.message.includes('No code found')) {
              console.error('Decode error:', error);
            }
          }
        });
      } catch (err) {
        console.error('Error starting camera:', err);
      }
    },
    setVideoDimensions() {
      const videoElement = this.$refs.video;
      videoElement.style.width = '100%';
      videoElement.style.height = '100%';
    },
    onDecode(decodedString) {
      const now = Date.now();

      if (decodedString === this.lastScanned && now - this.lastScanTime < 1000) return;

      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.lastScanned = decodedString;
        this.lastScanTime = now;
        this.$emit('scan', decodedString);

        this.showSuccessFlash = true;
        this.showSkuOverlay = true;

        const item = this.items.find(item => item.customLabels.includes(decodedString));
        this.scannedSku = item?.name || decodedString;

        setTimeout(() => (this.showSuccessFlash = false), 500);
        setTimeout(() => (this.showSkuOverlay = false), 1000);
      }, 200);
    },
    stopCamera() {
      if (this.codeReader) {
        this.codeReader.reset();
        this.codeReader = null;
        const stream = this.$refs.video?.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          this.$refs.video.srcObject = null;
        }
      }
    },
    toggleFullscreen() {
      const scannerElement = this.$refs.scanner;
      if (this.isFullscreen) {
        Object.assign(scannerElement.style, {
          width: `${this.originalPosition.width}px`,
          height: `${this.originalPosition.height}px`,
          left: `${this.originalPosition.x}px`,
          top: `${this.originalPosition.y}px`,
          position: 'absolute',
        });
        this.$emit('fullscreen-changed', false);
      } else {
        const rect = scannerElement.getBoundingClientRect();
        this.originalPosition = { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
        Object.assign(scannerElement.style, {
          width: 'calc(100vw - 20rem)',
          height: '100vh',
          left: '0',
          top: '0',
          position: 'fixed',
        });
        this.$emit('fullscreen-changed', true);
      }
      this.isFullscreen = !this.isFullscreen;
    },
    startDrag(event) {
      if (this.isFullscreen) return;
      this.isDragging = true;
      this.startX = event.clientX || event.touches[0].clientX;
      this.startY = event.clientY || event.touches[0].clientY;
      const rect = this.$refs.scanner.getBoundingClientRect();
      this.initialX = rect.left;
      this.initialY = rect.top;
      document.addEventListener('mousemove', this.drag);
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchmove', this.drag);
      document.addEventListener('touchend', this.stopDrag);
    },
    drag(event) {
      if (!this.isDragging) return;
      const clientX = event.clientX || event.touches[0].clientX;
      const clientY = event.clientY || event.touches[0].clientY;
      const dx = clientX - this.startX;
      const dy = clientY - this.startY;
      this.$refs.scanner.style.transform = `translate(${dx}px, ${dy}px)`;
    },
    stopDrag() {
      if (!this.isDragging) return;
      this.isDragging = false;
      document.removeEventListener('mousemove', this.drag);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchmove', this.drag);
      document.removeEventListener('touchend', this.stopDrag);
    },
    startScanning() {
      this.startCamera();
    },
  },
  mounted() {
    this.$nextTick(this.startScanning);
  },
  unmounted() {
    this.stopCamera();
  },
};
</script>
<style scoped>
.barcode-scanner {
  position: absolute;
  top: 0;
  right: 0;
  height: 150px;
  background-color: #0b0d18;
  z-index: 1000;
  overflow: hidden;
  cursor: move;
  transition: all 0.3s ease;
}

.barcode-scanner.fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  height: 100%;
  width: calc(100vw - 20rem);
  resize: none;
  transform: none;
}

.scanner {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.effect-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.guide-line {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: red;
  transform: translateY(-50%);
}

.fullscreen-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  padding: 10px;
  cursor: pointer;
  border-radius: 10rem;
}

.success-flash {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 255, 0, 0.3);
  pointer-events: none;
  z-index: 1;
}

.sku-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px;
  border-radius: 5px;
  z-index: 2;
  font-size: 18px;
  text-align: center;
}

.scan-instructions {
  position: absolute;
  top: 10%;
  width: 100%;
  text-align: center;
  color: white;
  font-size: 18px;
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: rgba(255, 0, 0, 0.8);
  animation: scan 2s infinite linear;
}

@keyframes scan {
  0% { top: 0; }
  100% { top: 100%; }
}

@keyframes flash {
  0% { background-color: rgba(0, 255, 0, 0.3); }
  100% { background-color: rgba(0, 255, 0, 0); }
}

.success-flash {
  animation: flash 0.5s ease-out;
}
</style>
